import React, { createContext, useContext, useState, ReactNode, useCallback, useEffect } from 'react';
import { COOKIE_CONFIG_SERVICE, COOKIE_MOBILE_VERSION } from '../components/constants';
import { ConfigValue } from '../services/config-service/types';
import { getConfigService } from '../services';
import { getCookie } from '../utils';
import { compareVersions } from 'compare-versions';

interface ConfigContextProps {
    isNewNavigation: boolean;
    commerceConfig: ConfigValue | undefined;
}

const ConfigContext = createContext<ConfigContextProps | undefined>(undefined);

export const useConfig = () => {
    const context = useContext(ConfigContext);
    if (!context) {
        throw new Error('useConfig must be used within a ConfigProvider');
    }
    return context;
};

export const ConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isNewNavigation, setIsNewNavigation] = useState<boolean>(false);
    const [commerceConfig, setCommerceConfig] = useState<ConfigValue>();
    const configServiceUrl = getCookie(COOKIE_CONFIG_SERVICE) || '';
    const mobileVersion = getCookie(COOKIE_MOBILE_VERSION);

    const fetchConfigService = useCallback(async () => {
        try {
            const response = await getConfigService(configServiceUrl);
            if (!Array.isArray(response)) {
                throw new Error('Failed to fetch config service.');
            }
            const commerceConfigResponse: ConfigValue = response.find(config => config.label === 'suites.ecommerce.config').value;
            if (commerceConfigResponse && mobileVersion && commerceConfigResponse.enableNewMenuNavigationVersion) {
                setIsNewNavigation(compareVersions(mobileVersion, commerceConfigResponse.enableNewMenuNavigationVersion) >= 0);
                setCommerceConfig(commerceConfigResponse);
            }
        } catch (error) {
            console.error('ConfigContext: Failed to fetch config service:', error);
        }
    }, []);

    useEffect(() => {
        if (configServiceUrl && mobileVersion) {
            void fetchConfigService();
        }
    }, []);

    return (
        <ConfigContext.Provider
            value={{
                isNewNavigation,
                commerceConfig
            }}
        >
            {children}
        </ConfigContext.Provider>
    );
};
